// Two things every WebGPU device this page creates should have, applied at the
// one moment that works.
//
// ORT-Web caches its device on the FIRST webgpu session create — which, in this
// app, can be the hair spec_mod OR the decoders, whichever loads first. A later
// patch (e.g. inside setupSharedOrtAdapter) is too late: the device already
// exists. ORT-Web 1.26's `ort.env.webgpu.adapter` is read-only, so handing ORT a
// pre-built adapter isn't an option; patching `GPUAdapter.prototype.requestDevice`
// at app boot is — hence the side-effect import at the top of `main.jsx`.
//
// WHAT IT NO LONGER DOES: raise `maxStorageBuffersPerShaderStage`. It used to
// request the adapter's MAXIMUM on every device, because a pass once bound more
// than WebGPU's default of 8. Nothing does any more — every shader in the repo
// binds 8 or fewer, including the pass 2 that is DERIVED at load time, and
// `liftStorageBuffers.test.mjs` pins all of them. Asking for more than you use is
// not free: the spec allows an implementation to hand back a slower device for a
// higher limit, and it made every consumer on the page pay for a ceiling one pass
// briefly wanted.
//
// What survives is the `?perf=1` half: `timestamp-query`, so the lifter can time
// pass1->pass2. Additive and opt-in, harmless to every other consumer.
//
// MOSTLY REDUNDANT NOW, and kept for the case it is not. `sharedGpuDevice.js`
// creates the page's one device with EVERY feature the adapter reports, which
// includes `timestamp-query`, and hands it to three and to ORT. This still
// covers a device created while sharing is off (`?gpudevice=0`) or after the
// shared one failed — and it stays FIRST in main.jsx, because it has to wrap the
// native `requestDevice` before anything wraps it.
export function installWebGpuLimitPatch() {
  if (typeof GPUAdapter === 'undefined' || !GPUAdapter.prototype) return;
  if (GPUAdapter.prototype.__aosLimitsPatched) return;
  const orig = GPUAdapter.prototype.requestDevice;
  if (typeof orig !== 'function') return;
  const wantPerf = typeof location !== 'undefined' && /[?&]perf=1/.test(location.search);
  GPUAdapter.prototype.requestDevice = function requestDevice(descriptor = {}) {
    // The caller's own limits are passed through UNTOUCHED. A consumer that needs
    // more than the default asks for it itself, where the number sits next to the
    // pass that needs it and a test can pin the pair.
    let requiredFeatures = descriptor.requiredFeatures;
    if (wantPerf && this.features?.has?.('timestamp-query')
        && !(requiredFeatures && [...requiredFeatures].includes('timestamp-query'))) {
      requiredFeatures = [...(requiredFeatures ?? []), 'timestamp-query'];
    }
    if (requiredFeatures === descriptor.requiredFeatures) return orig.call(this, descriptor);
    return orig.call(this, { ...descriptor, requiredFeatures });
  };
  GPUAdapter.prototype.__aosLimitsPatched = true;
}

installWebGpuLimitPatch();
